import { t } from '@grafana/i18n';
import { ConfirmModal } from '@grafana/ui';
import { useDeleteQueryMutation } from 'app/extensions/api/clients/queries/v1beta1';

type Props = {
  isOpen: boolean;
  /** Query uids to delete. Single callers pass `[uid]`; the bulk action passes all selected uids. */
  uids: string[];
  onDismiss: () => void;
  /**
   * Called after the deletes settle. `deletedUids` are the queries actually removed (a subset on
   * partial failure, empty if all failed) so the parent can prune those from any selection;
   * `allDeleted` is true only when every requested uid succeeded, so the parent can decide whether to
   * close. Reporting by success — not by which rows remain visible — keeps a filtered list correct.
   */
  onDeleted?: (deletedUids: string[], allDeleted: boolean) => void;
  /** Extra modal styling — used by the details-panel footer to center the modal inside its parent drawer. */
  modalClass?: string;
};

// Shared confirmation modal for deleting one or more saved queries. Owns the delete mutation so every
// call site (page table, bulk actions, drawer details footer) shares the same wording and loading behavior.
export function DeleteSavedQueriesModal({ isOpen, uids, onDismiss, onDeleted, modalClass }: Props) {
  const [deleteQuery, { isLoading }] = useDeleteQueryMutation();
  const isMultiple = uids.length > 1;

  const handleConfirm = async () => {
    // allSettled so one failure doesn't stop the remaining deletions. unwrap() makes a failed delete
    // reject (RTK otherwise resolves to { error }), so we can tell which settled results failed.
    // The queries client already toasts per delete (success + failure), so we don't surface errors here.
    const results = await Promise.allSettled(uids.map((uid) => deleteQuery({ name: uid }).unwrap()));

    // Report exactly which queries were removed so the parent can drop them from any selection; on
    // partial failure the failed rows stay selected so the user can retry just those.
    const deletedUids = uids.filter((_, index) => results[index].status === 'fulfilled');
    onDeleted?.(deletedUids, deletedUids.length === uids.length);
  };

  const title = isMultiple
    ? t('query-library.delete-saved-queries-modal.title-plural', 'Delete queries')
    : t('query-library.delete-saved-queries-modal.title', 'Delete query');

  const body = isMultiple
    ? t(
        'query-library.delete-saved-queries-modal.body-text-plural',
        "You're about to remove the selected saved queries. This action cannot be undone. Do you want to continue?"
      )
    : t(
        'query-library.delete-saved-queries-modal.body-text',
        "You're about to remove this saved query. This action cannot be undone. Do you want to continue?"
      );

  const confirmText = isLoading
    ? t('query-library.delete-saved-queries-modal.confirm-button-loading', 'Deleting…')
    : isMultiple
      ? t('query-library.delete-saved-queries-modal.confirm-button-plural', 'Delete queries')
      : t('query-library.delete-saved-queries-modal.confirm-button', 'Delete query');

  return (
    <ConfirmModal
      isOpen={isOpen}
      title={title}
      body={body}
      confirmText={confirmText}
      modalClass={modalClass}
      onConfirm={handleConfirm}
      onDismiss={onDismiss}
    />
  );
}
